/**
 * Aspid / core: общий скрипт всех страниц.
 *
 *   - шапка: класс scrolled при прокрутке
 *   - дроверы каталога и корзины, оверлей поиска (data-action)
 *   - живой поиск (/ajax/search) в оверлее
 *   - корзина: состояние, бейдж, кнопки [data-action="cart-add"]
 *   - reveal-анимации при появлении в вьюпорте
 *   - маска телефона для [data-phone-mask]
 *
 * Общие помощники доступны страничным бандлам через window.rt:
 * api, t, price, notify, cart, phoneMask, reveal.
 */
import { api } from './modules/api';
import { t, price } from './modules/i18n';
import { notify } from './modules/notify';
import { cart } from './modules/cart';
import { phoneMask } from './modules/phone';

const body = document.body;

// ---------------------------------------------------------------------
// Дроверы и оверлеи
// ---------------------------------------------------------------------

const panels = {
    catalog: [ 'catalogDrawer', 'catalogBackdrop' ],
    cart:    [ 'cartDrawer', 'cartBackdrop' ],
    search:  [ 'searchOverlay' ],
};

function togglePanel( name, open ) {

    ( panels[ name ] || [] ).forEach( id => {

        const el = document.getElementById( id );

        if( el ) {
            el.classList.toggle( 'active', open );
            el.setAttribute( 'aria-hidden', open ? 'false' : 'true' );
        }
    } );

    body.style.overflow = open ? 'hidden' : '';

    if( name === 'search' && open )
        setTimeout( () => document.getElementById('searchInput')?.focus(), 250 );

    if( name === 'cart' && open && ! cart.state.loaded )
        cart.load();
}

function closeAll() {

    Object.keys( panels ).forEach( name => togglePanel( name, false ) );
}

// ---------------------------------------------------------------------
// Reveal
// ---------------------------------------------------------------------

function reveal( root = document ) {

    const nodes = root.querySelectorAll('.reveal:not(.active)');

    if( ! ( 'IntersectionObserver' in window ) ) {
        nodes.forEach( el => el.classList.add('active') );
        return;
    }

    const observer = new IntersectionObserver( ( entries ) => {
        entries.forEach( ( entry, i ) => {
            if( entry.isIntersecting ) {
                setTimeout( () => entry.target.classList.add('active'), i * 40 );
                observer.unobserve( entry.target );
            }
        } );
    }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' } );

    nodes.forEach( el => observer.observe( el ) );
}

// ---------------------------------------------------------------------
// Поиск
// ---------------------------------------------------------------------

let searchTimer = null;

function escape( text ) {

    return String( text ).replace( /[&<>"']/g, ch => ( {
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    } )[ ch ] );
}

async function liveSearch( query ) {

    const box = document.querySelector('[data-search-results]');

    if( ! box )
        return;

    if( query.length < 2 ) {
        box.innerHTML = '';
        return;
    }

    try {

        const data = await api.get( 'search', { query } );

        if( ! data.items?.length ) {
            box.innerHTML = `<div class="search-empty">${ t('catalog.nothing_found') }</div>`;
            return;
        }

        box.innerHTML = data.items.map( item => `
            <a class="search-result" href="${ window.baseURL }/${ escape( item.slug ) }">
                ${ item.image_thumb ? `<img src="${ escape( item.image_thumb ) }" alt="">` : '' }
                <span class="search-result-title">${ escape( item.title ) }</span>
                <span class="search-result-price">${ price( item.sale_price > 0 ? item.sale_price : item.price ) }</span>
            </a>` ).join('');

    } catch( e ) {
        box.innerHTML = '';
    }
}

// ---------------------------------------------------------------------
// Делегирование кликов
// ---------------------------------------------------------------------

async function handleAction( el, event ) {

    const action = el.dataset.action;

    switch( action ) {

        case 'catalog-open': togglePanel( 'catalog', true ); break;
        case 'catalog-close': togglePanel( 'catalog', false ); break;
        case 'cart-open': togglePanel( 'cart', true ); break;
        case 'cart-close': togglePanel( 'cart', false ); break;
        case 'search-open': togglePanel( 'search', true ); break;
        case 'search-close': togglePanel( 'search', false ); break;

        case 'cart-add': {

            event.preventDefault();

            const productId   = Number( el.dataset.productId );
            const variationId = Number( el.dataset.variationId || 0 );

            el.disabled = true;

            try {
                await cart.add( productId, variationId, Number( el.dataset.quantity || 1 ) );
                togglePanel( 'cart', true );
            } catch( e ) {
                notify( e.message, 'error' );
            }

            el.disabled = false;
            break;
        }

        case 'cart-qty': {

            const row = el.closest('[data-variation-id]');
            const qty = row?.querySelector('.qty-value');

            if( ! row || ! qty )
                break;

            const next = Number( qty.textContent ) + Number( el.dataset.delta );

            try {
                await cart.quantity( Number( row.dataset.variationId ), Math.max( 0, next ) );
            } catch( e ) {
                notify( e.message, 'error' );
            }
            break;
        }

        case 'cart-remove': {

            const row = el.closest('[data-variation-id]');

            if( ! row )
                break;

            try {
                await cart.remove( Number( row.dataset.variationId ) );
            } catch( e ) {
                notify( e.message, 'error' );
            }
            break;
        }
    }
}

document.addEventListener( 'click', ( event ) => {

    const el = event.target.closest('[data-action]');

    if( el )
        handleAction( el, event );

    const hint = event.target.closest('[data-search-hint]');

    if( hint ) {

        const input = document.getElementById('searchInput');

        if( input ) {
            input.value = hint.dataset.searchHint;
            input.focus();
            liveSearch( input.value );
        }
    }
} );

document.addEventListener( 'keydown', ( event ) => {

    if( event.key === 'Escape' )
        closeAll();
} );

// ---------------------------------------------------------------------
// Инициализация
// ---------------------------------------------------------------------

const header = document.getElementById('header');

if( header )
    window.addEventListener( 'scroll', () => {
        header.classList.toggle( 'scrolled', window.scrollY > 10 );
    }, { passive: true } );

document.getElementById('searchInput')?.addEventListener( 'input', ( event ) => {

    clearTimeout( searchTimer );

    searchTimer = setTimeout( () => liveSearch( event.target.value.trim() ), 250 );
} );

// бейдж корзины - из серверного снапшота, дровер догружается при открытии
if( window.shop?.cart )
    document.querySelectorAll('[data-cart-count]').forEach( el => {
        el.textContent = window.shop.cart.item_count;
    } );

reveal();
phoneMask();

window.rt = { api, t, price, notify, cart, phoneMask, reveal, togglePanel };
